import type {
  Campaign,
  IBaseCampaignVersion,
  IBaseCampaignVersionSettings,
  Variant,
} from '~/modules/campaign/types';
import type { ICampaignGroup } from '~/modules/campaign-group/types';
import type { ChoiceOptionItem } from '~/presentation/ui';

import { sortByConditions, deepCopy } from '~/utils';
import { SETTING_GOAL } from '~/constants/campaign';

const DEFAULT_TRAFFIC_RATE = 0.5;
const DEFAULT_LABELS = {
  DEVICES: 'All devices',
  VISITORS: 'All visitors',
  SOURCES: 'All sources',
} as const;

const findVersionByAlias = (
  versions: IBaseCampaignVersion[],
  alias?: string,
): IBaseCampaignVersion => {
  const sortedVersions = sortByConditions<IBaseCampaignVersion>(versions, [
    { attr: 'version', order: 'asc' },
  ]);

  return sortedVersions.find((v) => v.version === alias) || sortedVersions[0];
};

const extractVersionSettings = (version: IBaseCampaignVersion): IBaseCampaignVersionSettings => {
  return {
    trafficRate: version?.settings?.traffic_rate,
    targetDevices: version?.settings?.target_devices,
    targetVisitorTypes: version?.settings?.target_visitor_types,
    targetChannels: version?.settings?.target_channels,
    targetCountries: version?.settings?.target_countries,
    campaignVariants: version?.settings?.campaign_variants,
    ...version?.settings,
  } as IBaseCampaignVersionSettings;
};

const getGoalLabel = (
  versionSetting?: IBaseCampaignVersionSettings,
  campaign?: Campaign | ICampaignGroup,
): string => {
  const priority = versionSetting?.priority || campaign?.priority;
  return SETTING_GOAL.find((item) => item.value === priority)?.label ?? '';
};

const mapSettingToLabels = <T extends string>(
  values: (T | undefined | 'all')[],
  options: ChoiceOptionItem<T>[],
  defaultLabel: string,
): string[] => {
  if (values.includes('all' as T)) {
    return [defaultLabel];
  }

  return options.filter((option) => values.includes(option.value)).map((option) => option.label);
};

const calculateTrafficSplit = (trafficRate: number): number[] => {
  return [Math.round(trafficRate * 100), Math.round((1 - trafficRate) * 100)];
};

const getBaseTrafficRate = (campaignData: Campaign | ICampaignGroup): number => {
  if ('variants' in campaignData) {
    return campaignData.variants?.find((v) => v?.isOrigin)?.trafficRate ?? DEFAULT_TRAFFIC_RATE;
  }

  if ('trafficRate' in campaignData) {
    return campaignData.trafficRate ?? DEFAULT_TRAFFIC_RATE;
  }

  return DEFAULT_TRAFFIC_RATE;
};

const applyTrafficRateToVariants = (
  campaign: Campaign,
  trafficRateMap: Record<string, number>,
): Campaign => {
  return {
    ...campaign,
    variants: campaign.variants?.map((variant) => ({
      ...variant,
      trafficRate: trafficRateMap[variant?.id ?? ''],
    })) as Variant[],
  };
};

const getTrafficSplitFromVersion = (
  versionSetting?: IBaseCampaignVersionSettings,
  campaign?: Campaign | ICampaignGroup,
): number[] | undefined => {
  if (!versionSetting?.trafficRate || !campaign) {
    return undefined;
  }

  const { trafficRate } = versionSetting;

  // Campaigns keep their split per variant, campaign groups a single rate — a per-variant map on a group
  // would end up assigned as-is below and read back as NaN.
  if (typeof trafficRate === 'object' && 'variants' in campaign) {
    const updatedCampaign = applyTrafficRateToVariants(deepCopy(campaign), trafficRate);
    return calculateTrafficSplit(getBaseTrafficRate(updatedCampaign));
  }

  const campaignCopy = deepCopy(campaign) as ICampaignGroup;
  campaignCopy.trafficRate = trafficRate as number;
  return calculateTrafficSplit(getBaseTrafficRate(campaignCopy));
};

export {
  DEFAULT_LABELS,
  findVersionByAlias,
  extractVersionSettings,
  getGoalLabel,
  mapSettingToLabels,
  calculateTrafficSplit,
  getBaseTrafficRate,
  applyTrafficRateToVariants,
  getTrafficSplitFromVersion,
};
